import { readInventoryField } from '../compile/inventory-raw-readers';
import { readOverrideEntryAbilityId } from '../compile/layer-cell-readers';
import { asRow } from '../minigames/rows';

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();

/** undefined means the pending inventory is not available to prove presence or absence. */
function inventoryWeaponMembership(actor: Row, id: string): boolean | undefined {
    if (ftLayerEntries(actor, 'inventory').some(row => upper(ftInventoryField(row, 'inventory_id')) === id)) return true;
    const held = asRow(actor._inventory_held);
    const previous = held ? window.ftReadHeldInventoryBase?.(upper(held.entity_id)) : undefined;
    const envelope = asRow(actor._inventory_base)
        ?? (previous?.base_rev === held?.base_rev && previous?.engine === held?.engine ? previous : undefined);
    const inventory = asRow(envelope?.base)?.inventory;
    if (Array.isArray(inventory)) return inventory.some(row => upper(readInventoryField(asRow(row), 'inventory_id')) === id);
    return undefined;
}

/** A definition warm-up skips only an inventory identity positively present in held inputs. */
export function heldInventoryWeaponSelected(actor: Row | null, weaponId: string): boolean {
    const id = upper(weaponId);
    return !!id && !!actor && inventoryWeaponMembership(actor, id) === true;
}

/** A pending facet still identifies inventory clicks; never treat an instance as a catalog ability. */
export function inventoryWeaponSelected(actor: Row | null, weaponId: string): boolean {
    const id = upper(weaponId);
    if (!id || !actor) return false;
    const membership = inventoryWeaponMembership(actor, id);
    if (membership !== undefined) return membership;
    const held = asRow(actor._inventory_held);
    if (!held) return false;
    // A catalog miss is not an inventory identity. The actor's compiled buckets
    // identify its abilities even before their definitions enter the object cache.
    const abilities = asRow(window.ftComputedValue?.(actor, 'creature', 'abilities'));
    if ([...Object.keys(abilities ?? {}), 'spells_known', 'curses_known'].some(key =>
        ftLayerEntries(actor, key).some(entry => upper(readOverrideEntryAbilityId(entry)) === id))) return false;
    const definition = (window as Window & { FateTide?: { objectCache?: ReadonlyMap<string, unknown> } }).FateTide?.objectCache?.get(id);
    return !ftItemsCol(definition, 'item_type') || upper(ftInventoryField(definition, 'inventory_id')) === id;
}

/** Join pending instance paint or an attached attack's inventory selector. Definitions are preloaded separately.
 * False retires this attempt even if the inventory paints immediately after the deadline. */
export async function hydrateColdWeaponBinding(actorId: string, weaponId: string, actorRow: () => Row | null, attachedAbility = false): Promise<boolean> {
    const actor = actorRow();
    if (actor?._inventory_refusal !== 'unported:inventory-base-pending') return true;
    const selected = inventoryWeaponSelected(actor, weaponId);
    if (!attachedAbility && !selected) return true;
    let deadline: ReturnType<typeof setTimeout> | undefined, expired = false;
    try {
        return await Promise.race([
            new Promise<boolean>(resolve => { deadline = setTimeout(() => { expired = true; resolve(false); }, 15_000); }),
            (async () => {
                let ready: Row | null = actor;
                do {
                    await window.ftAdoptInventoryBase?.(ready);
                    if (expired) return false;
                    await window.ftWaitForInventoryBase?.(actorId);
                    if (expired) return false;
                    ready = actorRow();
                    if (ready?._inventory_refusal !== 'unported:inventory-base-pending') break;
                    // Let the shared deadline run even if a pending surface has no
                    // registered adoption yet and both bridges settle immediately.
                    await new Promise<void>(resolve => setTimeout(resolve, 0));
                } while (!expired);
                return !expired && !!ready && typeof ready._inventory_refusal !== 'string'
                    && (!selected || ftLayerEntries(ready, 'inventory').some(row => upper(ftInventoryField(row, 'inventory_id')) === upper(weaponId)));
            })(),
        ]);
    } catch { return false; }
    finally { clearTimeout(deadline); }
}
